import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Cube } from "@phosphor-icons/react";
import { toast } from "sonner";

const LC_TONE = { aktiv: "green", abgekuendigt: "amber", ersetzt: "blue", gesperrt: "red" };

export default function Material() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sku: "", name: "", category: "Allgemein", manufacturer: "", unit: "Stk", spare_part: false, min_qty: 0, lifecycle_status: "aktiv", compatibility: [], suppliers: [] });

  const load = useCallback(() => api.get("/materials").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try { await api.post("/materials", { ...form, min_qty: Number(form.min_qty) || 0 }); toast.success("Material angelegt"); setOpen(false); setForm({ sku: "", name: "", category: "Allgemein", manufacturer: "", unit: "Stk", spare_part: false, min_qty: 0, lifecycle_status: "aktiv", compatibility: [], suppliers: [] }); load(); }
    catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  if (!items) return <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;

  return (
    <div>
      <PageHeader title="Material" subtitle={`${items.length} Materialstämme · PIM · Kompatibilität · Lebenszyklus`}>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-material-button" className="gap-2"><Plus size={18} weight="bold" />Neues Material</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="material-dialog">
            <DialogHeader><DialogTitle className="font-heading">Material anlegen</DialogTitle></DialogHeader>
            <DialogDescription>Materialstamm im PIM erfassen.</DialogDescription>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label2>SKU *</Label2><Input data-testid="material-sku" value={form.sku} onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Einheit</Label2><Input value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))} /></div>
              <div className="space-y-1.5 col-span-2"><Label2>Name *</Label2><Input data-testid="material-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Kategorie</Label2><Input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Hersteller</Label2><Input value={form.manufacturer} onChange={(e) => setForm((f) => ({ ...f, manufacturer: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Mindestbestand</Label2><Input type="number" value={form.min_qty} onChange={(e) => setForm((f) => ({ ...f, min_qty: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label2>Lebenszyklus</Label2>
                <Select value={form.lifecycle_status} onValueChange={(v) => setForm((f) => ({ ...f, lifecycle_status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["aktiv", "abgekuendigt", "ersetzt", "gesperrt"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <label className="col-span-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.spare_part} onChange={(e) => setForm((f) => ({ ...f, spare_part: e.target.checked }))} />Ersatzteil</label>
            </div>
            <DialogFooter><Button data-testid="material-save" onClick={submit} disabled={!form.sku || !form.name}>Anlegen</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <Card className="divide-y divide-border rounded-md border">
        {items.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Kein Material.</div>}
        {items.map((m) => (
          <div key={m.id} data-testid="material-row" className="flex items-center justify-between gap-3 px-5 py-4">
            <div className="flex min-w-0 items-center gap-3">
              <Cube size={20} weight="duotone" className="shrink-0 text-primary" />
              <div className="min-w-0">
                <div className="truncate font-semibold">{m.name} {m.spare_part && <span className="text-xs text-primary">· Ersatzteil</span>}</div>
                <div className="truncate font-mono text-xs text-muted-foreground">{m.sku} · {m.manufacturer || "—"} · {(m.compatibility || []).join(", ") || "keine Kompatibilität"}</div>
              </div>
            </div>
            <StatusBadge tone={LC_TONE[m.lifecycle_status]}>{m.lifecycle_status}</StatusBadge>
          </div>
        ))}
      </Card>
    </div>
  );
}
